import { FormProvider, useForm } from 'react-hook-form';
import Modal from '../../../components/common/Modal';
import TextInput from '../../../components/common/TextInput';
import Button from '../../../components/common/button';
import { toast } from 'react-toastify';
import useAddNewCategory from '../../../hooks/use-add-new-category';
import useUpdateCategory from '../../../hooks/use-update-category';
import Switch from '../../../components/common/Switch';
import { useState, useEffect } from 'react';
import useGetComponentsQuery from '../../../hooks/use-get-components';

interface IFormData {
  categoryName: string;
  displayName: string;
  name: string;
  goTo: string;
  viewRank: number;
  isVisible: boolean;
  isSubmenu: boolean;
  isDining: boolean;
}

interface AddCategoryModalProps {
  isOpen: boolean;
  isEditCategory?: boolean;
  closeModal: () => void;
  onSuccess: () => void;
}

const AddCategoryModal = ({
  isOpen = false,
  isEditCategory = false,
  closeModal,
  onSuccess
}: AddCategoryModalProps) => {
  const { mutate: addNewCategory, isPending: isAddPending } = useAddNewCategory();
  const { mutate: updateCategory, isPending: isUpdatePending } = useUpdateCategory();
  const { data: componentsData, isPending: isComponentsPending } = useGetComponentsQuery({ hotelId: 'c102' });
  
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  
  const components = componentsData?.result?.data?.cateogry || [];

  const methods = useForm<IFormData>({
    defaultValues: {
      categoryName: '',
      displayName: '',
      name: '',
      goTo: '',
      viewRank: 1,
      isVisible: true,
      isSubmenu: true,
      isDining: false
    }
  });

  const { handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    if (selectedCategory && isEditCategory) {      
      setValue('categoryName', selectedCategory.categoryName);
      setValue('displayName', selectedCategory.displayName);
      setValue('isVisible', selectedCategory.isVisible ?? true);
    }
  }, [selectedCategory, isEditCategory, setValue]);

  const handleFormSubmit = (data: IFormData) => {
    if (isEditCategory && selectedCategory) {
      const payload = {
        categoryName: data.categoryName,
        displayName: data.displayName,
        isVisible: data.isVisible
      };

      updateCategory({ data: payload, id: selectedCategory._id }, {
        onSuccess: (res) => {          
          toast.success(res?.message || 'Category updated successfully');
          onSuccess();
          closeModal();
          setSelectedCategory(null)
          reset({
            categoryName: '',
            displayName: '',
            name: '',
            goTo: '',
            viewRank: 1,
            isVisible: true,
            isSubmenu: true,
            isDining: false
          });
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Failed to update category');
        }
      });
    } else {
      const payload = {
        categoryName: data.categoryName,
        componentName: "submenu",
        data: {
          name: data.name,
          goTo: data.goTo,
          viewRank: data.viewRank
        },
        isVisible: data.isVisible,
        isSubmenu: data.isSubmenu,
        isDining: data.isDining
      };

      addNewCategory(payload, {
        onSuccess: () => {
          toast.success('Category added successfully');
          onSuccess();
          closeModal();
          reset({
            categoryName: '',
            displayName: '',
            name: '',
            goTo: '',
            viewRank: 1,
            isVisible: true,
            isSubmenu: true,
            isDining: false
          });
        },
        onError: (err: any) => {          
          toast.error(err?.response?.data?.message || 'Failed to add category');
        }
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      title={isEditCategory ? 'Edit Categories' : 'Add New Category'}
      className={`${isEditCategory ? 'w-3/4' : 'w-1/3'} max-w-6xl`}
    >
      <div className='h-96 flex'>
        {isEditCategory && (
          <div className='w-1/3 border-r pr-4 overflow-y-auto'>
            <h3 className='font-medium mb-4'>Categories</h3>
            <div className='space-y-2'>
              {components.map(component => (
                <div
                  key={component._id}
                  className={`p-3 rounded cursor-pointer transition ${
                    selectedCategory?._id === component._id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedCategory(component)}
                >
                  <div className='font-medium'>{component.displayName}</div>
                  <div className='text-sm text-gray-500'>{component.categoryName}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={`${isEditCategory ? 'w-2/3 pl-4' : 'w-full'} overflow-y-auto`}>
          <FormProvider {...methods}>
            <form className="space-y-4">
              <TextInput
                name="categoryName"
                label="Category Name"
                placeholder="Enter category name"
                rules={{ required: 'Category name is required' }}
                required
                disabled={isEditCategory || isAddPending || isUpdatePending || isComponentsPending}
              />
              
              <TextInput
                name="displayName"
                label="Display Name"
                placeholder="Enter display name"
                rules={{ required: 'Display name is required' }}
                required
                disabled={isAddPending || isUpdatePending || isComponentsPending}
              />

              <Switch
                label="Show in Tabs"
                name="isVisible"
                checked={watch('isVisible')}
                disabled={isAddPending || isUpdatePending || isComponentsPending}
              />

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="default"
                  onClick={closeModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  onClick={handleSubmit(handleFormSubmit)}
                  disabled={isAddPending || isUpdatePending || isComponentsPending || (isEditCategory && !selectedCategory)}
                >
                  {isEditCategory ? 'Update Category' : 'Add Category'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </Modal>
  );
};

export default AddCategoryModal;